/**
 * BurndownChart - Sprint burndown visualization
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { analyticsApi, type BurndownData } from '@services/api';

interface BurndownChartProps {
  projectId: string;
  sprintId?: string;
}

export default function BurndownChart({ projectId, sprintId }: BurndownChartProps) {
  const { t } = useTranslation();
  const [data, setData] = useState<BurndownData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const burndownData = await analyticsApi.getBurndown(projectId, sprintId);
        setData(burndownData);
      } catch (err) {
        setError(t('analytics.burndownError'));
      } finally {
        setIsLoading(false);
      }
    };

    void fetchData();
  }, [projectId, sprintId, t]);

  const { maxValue, chartHeight } = useMemo(() => {
    if (data.length === 0) return { maxValue: 0, chartHeight: 256 };

    const max = Math.max(...data.map(d => Math.max(d.idealRemaining, d.actualRemaining)));
    return { maxValue: max || 1, chartHeight: 256 };
  }, [data]);

  const getPointX = useCallback((index: number) => {
    if (data.length <= 1) return 50;
    return (index / (data.length - 1)) * 100;
  }, [data.length]);

  const getPointY = useCallback((value: number) => {
    return chartHeight - (value / maxValue) * chartHeight;
  }, [chartHeight, maxValue]);

  const idealPath = useMemo(() => {
    if (data.length === 0) return '';
    return data
      .map((d, i) => `${i === 0 ? 'M' : 'L'} ${getPointX(i)}% ${getPointY(d.idealRemaining)}`)
      .join(' ');
  }, [data, getPointX, getPointY]);

  const actualPath = useMemo(() => {
    if (data.length === 0) return '';
    return data
      .map((d, i) => `${i === 0 ? 'M' : 'L'} ${getPointX(i)}% ${getPointY(d.actualRemaining)}`)
      .join(' ');
  }, [data, getPointX, getPointY]);

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (error || data.length === 0) {
    return (
      <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6">
        <div className="text-center text-neutral-500">
          {error ?? t('analytics.noData')}
        </div>
      </div>
    );
  }

  const today = new Date().toISOString().split('T')[0];
  const todayIndex = data.findIndex(d => d.date === today);
  const todayData = todayIndex >= 0 ? data[todayIndex] : null;
  const behindSchedule = todayData ? todayData.actualRemaining > todayData.idealRemaining : false;

  return (
    <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">
            {t('analytics.burndown')}
          </h3>
          <p className="text-sm text-neutral-500">
            {t('analytics.burndownDescription')}
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="w-6 h-0.5 bg-neutral-400" style={{ background: 'linear-gradient(90deg, #9ca3af 50%, transparent 50%)', backgroundSize: '8px 2px' }} />
            <span className="text-xs text-neutral-500">{t('analytics.ideal')}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-6 h-0.5 bg-primary-500" />
            <span className="text-xs text-neutral-500">{t('analytics.actual')}</span>
          </div>
        </div>
      </div>

      {/* Status indicator */}
      {todayIndex >= 0 && (
        <div className={`mb-4 px-3 py-2 rounded-lg text-sm ${
          behindSchedule
            ? 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400'
            : 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400'
        }`}>
          {behindSchedule ? t('analytics.behindSchedule') : t('analytics.onTrack')}
        </div>
      )}

      {/* Chart */}
      <div className="relative" style={{ height: chartHeight }}>
        {/* Grid lines */}
        <div className="absolute inset-0 flex flex-col justify-between pointer-events-none">
          {[0, 25, 50, 75, 100].map((pct) => (
            <div
              key={pct}
              className="border-t border-neutral-100 dark:border-neutral-700"
            />
          ))}
        </div>

        {/* Y-axis labels */}
        <div className="absolute left-0 top-0 bottom-0 -ml-12 w-10 flex flex-col justify-between text-xs text-neutral-500 text-right">
          <span>{maxValue}</span>
          <span>{Math.round(maxValue * 0.75)}</span>
          <span>{Math.round(maxValue * 0.5)}</span>
          <span>{Math.round(maxValue * 0.25)}</span>
          <span>0</span>
        </div>

        {/* SVG Chart */}
        <svg className="w-full h-full" preserveAspectRatio="none">
          {/* Ideal line (dashed) */}
          <path
            d={idealPath}
            fill="none"
            stroke="#9ca3af"
            strokeWidth="2"
            strokeDasharray="8 4"
          />

          {/* Actual line */}
          <path
            d={actualPath}
            fill="none"
            stroke="#3b82f6"
            strokeWidth="2"
          />

          {/* Data points */}
          {data.map((d, i) => (
            <g key={i}>
              <circle
                cx={`${getPointX(i)}%`}
                cy={getPointY(d.actualRemaining)}
                r="4"
                fill="#3b82f6"
                className="hover:r-6 transition-all cursor-pointer"
              >
                <title>
                  {d.date}: {d.actualRemaining} {t('analytics.remaining')}
                </title>
              </circle>
            </g>
          ))}
        </svg>
      </div>

      {/* X-axis labels */}
      <div className="mt-2 flex justify-between text-xs text-neutral-500">
        {data.filter((_, i) => i % Math.ceil(data.length / 5) === 0 || i === data.length - 1).map((d, i) => (
          <span key={i}>{new Date(d.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>
        ))}
      </div>

      {/* Summary */}
      <div className="mt-6 pt-4 border-t border-neutral-200 dark:border-neutral-700 grid grid-cols-3 gap-4">
        <div className="text-center">
          <div className="text-2xl font-bold text-neutral-900 dark:text-white">
            {data[0]?.idealRemaining ?? 0}
          </div>
          <div className="text-xs text-neutral-500">{t('analytics.startPoints')}</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-neutral-900 dark:text-white">
            {data[data.length - 1]?.actualRemaining ?? 0}
          </div>
          <div className="text-xs text-neutral-500">{t('analytics.remaining')}</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-neutral-900 dark:text-white">
            {data.reduce((sum, d) => sum + d.completed, 0)}
          </div>
          <div className="text-xs text-neutral-500">{t('analytics.completed')}</div>
        </div>
      </div>
    </div>
  );
}
